import { FormField } from '@/components/ui/form-field'
import { Label } from '@/components/ui/label'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getAllTechnicalTemplates } from '@/modules/Settings/services'
import { TechnicalTemplate } from '@/modules/Settings/types'
import { Sparkles } from 'lucide-react'
import React, { useEffect } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface TemplateSelectorProps {
    formControl: UseFormReturn<any>
    productGroupName?: string
    onTemplateSelect: (template: TechnicalTemplate | null) => void
}

const TemplateSelector: React.FC<TemplateSelectorProps> = ({
    formControl,
    productGroupName,
    onTemplateSelect,
}) => {
    const { t } = useTranslation()

    const { data: templates = [] } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.TECHNICAL_TEMPLATES],
            queryFn: () => getAllTechnicalTemplates(),
        },
        {
            showLoading: false,
            showError: false,
        }
    )

    const templateOptions = templates
        .filter((t) => t.is_active)
        .map((t) => ({
            label: `${t.product_group_name} - ${t.template_name}`,
            value: t.technical_template_id,
        }))

    // Auto-select template matching product group
    useEffect(() => {
        if (productGroupName && templates.length > 0) {
            const matched = templates.find(
                (t) => t.product_group_name === productGroupName
            )
            if (matched) {
                formControl.setValue(
                    'template_id',
                    matched.technical_template_id
                )
                onTemplateSelect(matched)
            }
        }
    }, [productGroupName, templates])

    if (templates.length === 0) {
        return null
    }

    return (
        <div className="flex items-center gap-3 p-2.5 border border-primary/20 rounded-md bg-primary/5">
            <div className="flex items-center gap-1.5 shrink-0">
                <Sparkles className="h-3.5 w-3.5 text-primary" />
                <Label className="text-xs font-medium text-primary">
                    {t('use-template')}
                </Label>
            </div>
            <div className="flex-1">
                <FormField
                    name="template_id"
                    type="select"
                    control={formControl.control}
                    placeholder={t('select-template-placeholder')}
                    options={templateOptions}
                    onValueChange={(value: number | null) => {
                        if (value) {
                            const selected = templates.find(
                                (t) => t.technical_template_id === value
                            )
                            onTemplateSelect(selected || null)
                        } else {
                            onTemplateSelect(null)
                        }
                    }}
                />
            </div>
        </div>
    )
}

export default TemplateSelector
